import { format, isValid, parse, parseISO } from 'date-fns';

export const DISPLAY_EMPTY = '-';

function parseToDate(value?: string | null): Date | undefined {
  if (value == null) return undefined;
  const raw = String(value).trim();
  if (!raw) return undefined;

  if (/^\d{6}$/.test(raw)) {
    const parsed = parse(raw, 'yyyyMM', new Date());
    if (isValid(parsed)) return parsed;
  }

  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const iso = parseISO(normalized);
  if (isValid(iso)) return iso;

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const parsed = parse(raw.slice(0, 10), 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) return parsed;
  }

  if (/^\d{4}\.\d{2}\.\d{2}/.test(raw)) {
    const parsed = parse(raw.slice(0, 10), 'yyyy.MM.dd', new Date());
    if (isValid(parsed)) return parsed;
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? undefined : fallback;
}

/** YYYYMM / ISO / Date → `YYYY.MM` */
export function formatYearMonth(value?: string | null): string {
  if (value == null || String(value).trim() === '') return DISPLAY_EMPTY;
  const raw = String(value).trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 6) {
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}`;
  }
  if (/^\d{4}\.\d{2}$/.test(raw)) return raw;
  const date = parseToDate(raw);
  if (date) return format(date, 'yyyy.MM');
  return raw;
}

/** ISO / Date → `YYYY.MM.DD` */
export function formatDate(value?: string | null): string {
  if (value == null || String(value).trim() === '') return DISPLAY_EMPTY;
  const date = parseToDate(value);
  if (date) return format(date, 'yyyy.MM.dd');
  const raw = String(value).trim();
  const dashed = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dashed) {
    return `${dashed[1]}.${dashed[2]}.${dashed[3]}`;
  }
  return raw.slice(0, 10);
}

/** ISO / Date → `YYYY.MM.DD HH:mm:ss` */
export function formatDateTime(value?: string | null): string {
  if (value == null || String(value).trim() === '') return DISPLAY_EMPTY;
  const date = parseToDate(value);
  if (date) return format(date, 'yyyy.MM.dd HH:mm:ss');
  const raw = String(value).trim().replace('T', ' ');
  const dashed = raw.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (dashed) {
    const seconds = dashed[6] ?? '00';
    return `${dashed[1]}.${dashed[2]}.${dashed[3]} ${dashed[4]}:${dashed[5]}:${seconds}`;
  }
  return raw;
}

/** ISO / Date → `YYYY.MM.DD HH:mm` (초 없음) */
export function formatDateTimeMinute(value?: string | null): string {
  if (value == null || String(value).trim() === '') return DISPLAY_EMPTY;
  const date = parseToDate(value);
  if (date) return format(date, 'yyyy.MM.dd HH:mm');
  const formatted = formatDateTime(value);
  const withSeconds = formatted.match(
    /^(\d{4}\.\d{2}\.\d{2} \d{2}:\d{2}):\d{2}$/,
  );
  return withSeconds ? withSeconds[1] : formatted;
}
